import { OnboardingDevice } from '../models/Onboarding/OnboardingDevice'
import { OnboardingLocation } from '../models/Onboarding/OnboardingLocation'
import { OnboardingOrganization } from '../models/Onboarding/OnboardingOrganization'
import { OnboardingState } from '../models/Onboarding/OnboardingState'
import { HostOnboardingSteps, OnboardingSteps } from './constants'

export function getCurrentStepFromOnboardingState(
  state: OnboardingState,
): HostOnboardingSteps {
  if (!state.location.name || !state.location.description) {
    return HostOnboardingSteps.LocationName
  }

  if (
    !state.location.address &&
    !state.location.city &&
    !state.location.state &&
    !state.location.zipCode
  ) {
    return HostOnboardingSteps.Address
  }

  if (
    !state.location.address ||
    !state.location.city ||
    !state.location.state ||
    !state.location.zipCode
  ) {
    return HostOnboardingSteps.Address
  }

  if (state.location.imageUrls.length === 0) {
    return HostOnboardingSteps.LocationPhotos
  }

  if (!state.location.footTraffic) {
    return HostOnboardingSteps.FootTraffic
  }

  if (Object.keys(state.location.businessHours).length === 0) {
    return HostOnboardingSteps.SetBusinessHours
  }

  if (!state.isStripeOnboardingComplete) {
    return HostOnboardingSteps.BankingInfo
  }

  if (state.device) {
    if (state.device.imageUrl === null) {
      return HostOnboardingSteps.DevicePhotos
    }
    if (state.device.name === null) {
      return HostOnboardingSteps.DeviceName
    }
    if (state.device.screenSize === null) {
      return HostOnboardingSteps.DeviceSize
    }
    if (state.device.monthlyPrice === null) {
      return HostOnboardingSteps.SetPricing
    }
    if (state.device.approvalType === null) {
      return HostOnboardingSteps.ApprovalType
    }
  } else {
    return HostOnboardingSteps.DeviceSetupInstructions
  }

  return HostOnboardingSteps.Review
}

export function getCurrentStepFromAdvertiserOnboardingState(
  organization: OnboardingOrganization,
): OnboardingSteps {
  if (organization.name === '') {
    return OnboardingSteps.OrganizationName
  }
  return OnboardingSteps.Category
}

export const validateAddress = (location: OnboardingLocation): boolean => {
  return !!(
    location.address &&
    location.city &&
    location.state &&
    location.zipCode
  )
}

export const isCurrentStepValid = (
  currentStepNumber: number,
  location: OnboardingLocation,
  device: OnboardingDevice | null,
  isStripeOnboardingComplete?: boolean,
): boolean => {
  switch (currentStepNumber) {
    case HostOnboardingSteps.LocationName:
      return !!location.name && location.name.trim().length > 0
    case HostOnboardingSteps.LocationDescription:
      return !!location.description && location.description.trim().length > 0
    case HostOnboardingSteps.LocationPhotos:
      return location.imageUrls.length > 0
    case HostOnboardingSteps.FootTraffic:
      return !!location.footTraffic
    case HostOnboardingSteps.BankingInfo:
      return isStripeOnboardingComplete ?? false
    case HostOnboardingSteps.LinkDevice:
      return device?.id != null
    case HostOnboardingSteps.DeviceName:
      return !!device?.name
    case HostOnboardingSteps.DeviceSize:
      return device?.screenSize !== null
    case HostOnboardingSteps.SetBusinessHours:
      return !!Object.values(location.businessHours).some((day) => !day.closed)
    case HostOnboardingSteps.Address:
      return validateAddress(location)
    case HostOnboardingSteps.DevicePhotos:
      return device?.imageUrl != null
    case HostOnboardingSteps.SetPricing:
      return device?.monthlyPrice != null && device?.monthlyPrice > 0
    case HostOnboardingSteps.ApprovalType:
      return device?.approvalType != null
    case HostOnboardingSteps.AdvertiserRestrictions:
      return true
    default:
      break
  }
  return true
}

export const getTitleTextForCurrentStep = (
  currentStep: HostOnboardingSteps,
): string => {
  switch (currentStep) {
    case HostOnboardingSteps.LocationName:
      return 'What is the name of your location?'
    case HostOnboardingSteps.LocationDescription:
      return 'Tell us about your location'
    case HostOnboardingSteps.FootTraffic:
      return 'Daily Foot Traffic'
    case HostOnboardingSteps.BankingInfo:
      return 'Enter your banking info to accept payments from advertisers'
    case HostOnboardingSteps.DeviceSize:
      return 'How large is your screen?'
    case HostOnboardingSteps.LocationPhotos:
      return 'Add a photo of your location'
    case HostOnboardingSteps.DevicePhotos:
      return 'Add a photo of your device'
    case HostOnboardingSteps.DownloadTVAppInstructions:
      return 'Download our display app on your device'
    case HostOnboardingSteps.SetPricing:
      return 'Set your monthly rate per ad on your device'
    case HostOnboardingSteps.ApprovalType:
      return 'Decide how ads will be approved'
    case HostOnboardingSteps.AdvertiserRestrictions:
      return 'Select business categories you want to prevent from advertising on this device'
    default:
      break
  }
  return ''
}
